/**
 * TMS 타일 소스 처리기 — 소스 타일 fetch·디코드·4분면 점유 마스크·리프 캔버스 합성. 환경(fetch·디코드·캔버스)을 주입받는
 * 순수 처리기라 Worker 진입 파일과 메인 스레드 폴백이 같은 코드를 돌리고, 테스트는 fake 환경으로 검증한다.
 * 메시지 타입은 Worker 와 메인 사이의 규약이다. spec inspection3d-tms-imagery 「타일 합성과 점유 마스크 산출은 UI 스레드
 * 밖에서 한다」
 */
import type { TileRange } from "./webMercatorTiles";
import {
  buildTmsTileUrl,
  createLimiter,
  quadrantMaskFromRgba,
  SOURCE_TILE_PX,
  sourceRangeCanvasSize,
  sourceTileCanvasOffset,
  type TileOccupancy,
  type TileXY,
} from "./tmsTileSource";

export interface TileSourceConfig {
  baseUrl: string;
  projectId: number;
  layerName: string;
  concurrency: number;
}

/** 합성 결과 — Worker 는 전송된 비트맵, 메인 폴백은 캔버스 그대로. 둘 다 Material `image` 유니폼이 받는다 */
export type TileImage = ImageBitmap | HTMLCanvasElement;

/** 비트맵이면 닫아 메모리를 돌려준다 — 캔버스는 GC 에 맡긴다. 테스트 환경(node·jsdom)에는 `ImageBitmap` 이 없다 */
export function closeTileImage(image: TileImage | null): void {
  if (typeof ImageBitmap !== "undefined" && image instanceof ImageBitmap)
    image.close();
}

export interface ComposeResult {
  image: TileImage | null;
  occupancy: TileOccupancy[];
}

export type TileSourceRequest =
  | { id: number; kind: "probe"; level: number; x: number; y: number }
  | {
      id: number;
      kind: "compose";
      sourceLevel: number;
      range: TileRange;
      candidates: TileXY[];
    };

export type TileSourceResponse =
  | { id: number; kind: "probe"; occupancy: TileOccupancy | null }
  | ({ id: number; kind: "compose" } & ComposeResult);

export type TileWorkerInbound =
  | { kind: "init"; config: TileSourceConfig }
  | TileSourceRequest;

export type TileWorkerOutbound = TileSourceResponse | { kind: "unsupported" };

export interface TileSourceContext2D {
  clearRect(x: number, y: number, width: number, height: number): void;
  drawImage(
    image: ImageBitmap,
    dx: number,
    dy: number,
    dw: number,
    dh: number
  ): void;
  getImageData(
    x: number,
    y: number,
    width: number,
    height: number
  ): { data: ArrayLike<number> };
}

export interface TileSourceCanvas {
  getContext(
    type: "2d",
    options?: { willReadFrequently?: boolean }
  ): TileSourceContext2D | null;
}

export interface TileSourceEnv<TCanvas extends TileSourceCanvas> {
  fetch(url: string, init: RequestInit): Promise<Response>;
  decode(blob: Blob): Promise<ImageBitmap>;
  createCanvas(width: number, height: number): TCanvas;
  /**
   * 합성 캔버스를 메인 스레드로 넘길 이미지로 — 메인은 캔버스 그대로, Worker 는 ImageBitmap. WebGL 은 ImageBitmap 에
   * `UNPACK_FLIP_Y_WEBGL` 을 적용하지 않으므로 Worker 쪽은 비트맵을 만들 때 상하를 뒤집어 캔버스 경로와 같은 방향으로 맞춘다.
   */
  exportImage(canvas: TCanvas): TileImage | Promise<TileImage>;
  /** 해제된 뒤의 응답은 만들지 않는다 */
  isStopped(): boolean;
}

export interface TileSourceHandler {
  /** 타일 하나를 받아 점유만 돌려준다 — 네트워크 오류면 점유를 모르므로 null */
  probe(level: number, x: number, y: number): Promise<TileOccupancy | null>;
  /** 후보 타일들을 받아 격자에 합성하고, 받은 타일들의 점유를 함께 돌려준다 */
  compose(
    sourceLevel: number,
    range: TileRange,
    candidates: TileXY[]
  ): Promise<ComposeResult>;
}

interface FetchedTile {
  bitmap: ImageBitmap | null;
  occupancy: TileOccupancy | null;
}

const EMPTY_COMPOSE: ComposeResult = { image: null, occupancy: [] };

/** 2D 컨텍스트를 만들 수 없는 환경이면 null — 호출자가 다른 경로(메인 폴백)로 간다 */
export function createTileSourceHandler<TCanvas extends TileSourceCanvas>(
  config: TileSourceConfig,
  env: TileSourceEnv<TCanvas>
): TileSourceHandler | null {
  const probeCtx = env
    .createCanvas(SOURCE_TILE_PX, SOURCE_TILE_PX)
    .getContext("2d", { willReadFrequently: true });
  if (!probeCtx) return null;
  const limit = createLimiter(config.concurrency);

  const fetchTile = async (
    level: number,
    x: number,
    y: number
  ): Promise<FetchedTile> => {
    const url = buildTmsTileUrl(
      config.baseUrl,
      config.projectId,
      config.layerName,
      level,
      x,
      y
    );
    try {
      const response = await limit(() => env.fetch(url, { mode: "cors" }));
      // 서버가 타일을 주지 않으면 빈 타일로 확정한다 — 자식도 요청하지 않는다
      if (!response.ok) return { bitmap: null, occupancy: { level, x, y, mask: null } };
      if (env.isStopped()) return { bitmap: null, occupancy: null };
      const bitmap = await env.decode(await response.blob());
      probeCtx.clearRect(0, 0, SOURCE_TILE_PX, SOURCE_TILE_PX);
      probeCtx.drawImage(bitmap, 0, 0, SOURCE_TILE_PX, SOURCE_TILE_PX);
      const mask = quadrantMaskFromRgba(
        probeCtx.getImageData(0, 0, SOURCE_TILE_PX, SOURCE_TILE_PX).data,
        SOURCE_TILE_PX
      );
      return { bitmap, occupancy: { level, x, y, mask } };
    } catch {
      return { bitmap: null, occupancy: null };
    }
  };

  return {
    probe: async (level, x, y) => {
      const { bitmap, occupancy } = await fetchTile(level, x, y);
      bitmap?.close();
      return occupancy;
    },
    compose: async (sourceLevel, range, candidates) => {
      if (candidates.length === 0 || env.isStopped()) return EMPTY_COMPOSE;
      const { width, height } = sourceRangeCanvasSize(range);
      const canvas = env.createCanvas(width, height);
      const ctx = canvas.getContext("2d");
      if (!ctx) return EMPTY_COMPOSE;
      const occupancy: TileOccupancy[] = [];
      let drewAny = false;
      await Promise.all(
        candidates.map(async (tile) => {
          const fetched = await fetchTile(sourceLevel, tile.x, tile.y);
          if (fetched.occupancy) occupancy.push(fetched.occupancy);
          if (!fetched.bitmap) return;
          const { px, py } = sourceTileCanvasOffset(range, tile);
          ctx.drawImage(fetched.bitmap, px, py, SOURCE_TILE_PX, SOURCE_TILE_PX);
          fetched.bitmap.close();
          drewAny = true;
        })
      );
      const image =
        drewAny && !env.isStopped() ? await env.exportImage(canvas) : null;
      return { image, occupancy };
    },
  };
}
